import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Clock, ChevronRight, Award } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { universityCourseMeta } from '../data/university';

interface Category { index: number; label: string }
interface Level { index: number; label: string }

const UniversityPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('university');
  const categories = (t('categories', { returnObjects: true }) || []) as string[];
  const levels = (t('filters.levels', { returnObjects: true }) || []) as string[];
  const coursesCatalog = (t('courses', { returnObjects: true }) || {}) as Record<string, {
    title: string;
    description: string;
    technologies: string[];
    duration: string;
  }>;

  const [category, setCategory] = useState<Category | null>(null);
  const [level, setLevel] = useState<Level | null>(null);

  const courses = useMemo(
    () =>
      universityCourseMeta.filter((course) =>
        (category === null || course.categoryIndex === category.index) &&
        (level === null || course.levelIndex === level.index)
      ),
    [category, level],
  );

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-44 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-70" />
        <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[900px] h-[440px] rounded-full bg-gradient-to-r from-blue-600/30 via-cyan-500/25 to-emerald-500/20 blur-[120px] animate-aurora" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] mb-6">
              <GraduationCap className="h-4 w-4 text-cyan-300" />
              <span className="text-cyan-200 text-sm font-semibold tracking-wide uppercase">{t('page.badge')}</span>
            </div>
            <h1 className="font-display text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-gradient animate-shimmer">{t('page.title')}</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">{t('page.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                  category === null
                    ? 'bg-cyan-400 text-slate-950 border-cyan-400'
                    : 'bg-white/[0.03] text-slate-300 border-white/10 hover:border-cyan-400/40'
                }`}
              >
                {t('filters.allCategories')}
              </button>
              {categories.map((label, index) => (
                <button
                  key={label}
                  onClick={() => setCategory({ index, label })}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                    category?.index === index
                      ? 'bg-cyan-400 text-slate-950 border-cyan-400'
                      : 'bg-white/[0.03] text-slate-300 border-white/10 hover:border-cyan-400/40'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="hidden md:block h-5 w-px bg-white/10" />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setLevel(null)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                  level === null
                    ? 'bg-white text-slate-950 border-white'
                    : 'bg-white/[0.03] text-slate-300 border-white/10 hover:border-white/30'
                }`}
              >
                {t('filters.allLevels')}
              </button>
              {levels.map((label, index) => (
                <button
                  key={label}
                  onClick={() => setLevel({ index, label })}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                    level?.index === index
                      ? 'bg-white text-slate-950 border-white'
                      : 'bg-white/[0.03] text-slate-300 border-white/10 hover:border-white/30'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, index) => {
              const Icon = course.icon;
              const data = coursesCatalog[course.id];
              if (!data) return null;
              return (
                <motion.button
                  key={course.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/university/${course.id}`)}
                  className="group relative flex flex-col text-left rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-cyan-400/40 hover:bg-white/[0.06] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-400/10 border border-white/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-cyan-300" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 bg-white/[0.05] px-2.5 py-1 rounded-full">
                      {categories[course.categoryIndex]}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-white mb-2 leading-snug">{data.title}</h2>
                  <p className="text-sm text-slate-400 leading-relaxed mb-4 flex-1">{data.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {data.technologies.slice(0, 3).map((tech) => (
                      <span key={tech} className="text-xs text-cyan-300/90 bg-cyan-400/[0.08] px-2 py-0.5 rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4 text-slate-400">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-4 w-4" />
                        {data.duration}
                      </span>
                      <span>{levels[course.levelIndex]}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-cyan-300 transition-transform group-hover:translate-x-1" />
                  </div>
                </motion.button>
              );
            })}
          </div>

          <div className="mt-16 rounded-3xl border border-white/10 bg-gradient-to-br from-blue-600/90 to-cyan-600/90 p-8 md:p-12 text-center">
            <div className="inline-flex items-center gap-2 text-white/80 mb-3">
              <Award className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-wide">{t('cta.eyebrow')}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t('cta.title')}</h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">{t('cta.description')}</p>
            <button
              onClick={() => navigate('/training#training-request')}
              className="inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-3 rounded-full font-semibold hover:bg-slate-900 transition-colors"
            >
              {t('cta.button')}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UniversityPage;
